import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import type { WizardState } from './add-financial-year-wizard';

export interface WizardStepSummaryProps {
  wizardState: WizardState;
  accounts: Array<{ id: string; legalName: string; entityType: string }>;
  onCreate: () => void;
  onBack: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

/**
 * Wizard Step 4: Review & Create
 *
 * Review card showing all selections:
 * - Entity name
 * - Data classification + trust grade badge
 * - Period details (type, label, dates, months)
 * - Annualisation factor (if YTD/Interim)
 * - Entry method
 *
 * "Create Period" button triggers actual creation
 */
export const WizardStepSummary: React.FC<WizardStepSummaryProps> = ({
  wizardState,
  accounts,
  onCreate,
  onBack,
  onCancel,
  isSubmitting,
}) => {
  const selectedAccount = accounts.find((a) => a.id === wizardState.selectedAccountId);

  const getClassificationLabel = (classification?: string) => {
    const labels: Record<string, string> = {
      accountant_prepared: 'Accountant Prepared Financials',
      tax_return: 'Tax Return Data',
      management_accounts: 'Management Accounts',
      xero_finalised: 'Xero (Finalised)',
      xero_draft: 'Xero (Draft)',
    };
    return classification ? labels[classification] || classification : 'Not selected';
  };

  const getTrustGradeLabel = (grade?: string) => {
    return grade === 'credit_grade' ? 'Credit Grade' : 'Insight Grade';
  };

  const getTrustGradeBadgeColor = (grade?: string) => {
    return grade === 'credit_grade' ? 'bg-green-600' : 'bg-amber-600';
  };

  const getTrustGradeTooltip = (grade?: string) => {
    return grade === 'credit_grade'
      ? 'Automatically set from your Data Classification. Accountant-prepared financials and tax returns carry full weight in lender credit assessments.'
      : 'Automatically set from your Data Classification. Management accounts provide valuable insights but carry lower weight in lender credit assessments.';
  };

  const getPeriodTypeLabel = (type?: string) => {
    const labels: Record<string, string> = {
      historical: 'Full Year (Historical)',
      ytd: 'Year to Date',
      forecast: 'Forecast',
    };
    return type ? labels[type] || type : 'Not selected';
  };

  const getMethodLabel = (method?: string) => {
    const labels: Record<string, string> = {
      manual: 'Manual Entry',
      csv: 'CSV/Excel Import',
      pdf: 'PDF Extraction',
      xero: 'Xero Sync',
    };
    return method ? labels[method] || method : 'Not selected';
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold" data-testid="wizard-step-title">
          Review & Create
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Review your selections before creating the financial period
        </p>
      </div>

      <div className="space-y-4 border rounded-lg p-4">
        {/* Entity */}
        {selectedAccount && (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-sm font-medium text-muted-foreground">Entity</div>
            <div className="col-span-2 text-sm">{selectedAccount.legalName}</div>
          </div>
        )}

        {/* Data Classification */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Data Classification</div>
          <div className="col-span-2" data-testid="summary-data-classification">
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {getClassificationLabel(wizardState.dataClassification)}
              </span>
            </div>
          </div>
        </div>

        {/* Trust Grade */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Trust Grade</div>
          <div className="col-span-2" data-testid="summary-trust-grade">
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="default"
                  className={`cursor-help ${getTrustGradeBadgeColor(wizardState.trustGrade)}`}
                  data-testid="summary-trust-grade-badge"
                >
                  {getTrustGradeLabel(wizardState.trustGrade)}
                </Badge>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-xs"
                data-testid="summary-trust-grade-tooltip"
              >
                {getTrustGradeTooltip(wizardState.trustGrade)}
              </TooltipContent>
            </Tooltip>
            <p className="text-xs text-muted-foreground mt-1" data-testid="summary-trust-grade-source">
              Derived from: {getClassificationLabel(wizardState.dataClassification)}
            </p>
          </div>
        </div>

        <hr />

        {/* Period Type */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Period Type</div>
          <div className="col-span-2 text-sm">
            {getPeriodTypeLabel(wizardState.periodType)}
          </div>
        </div>

        {/* Period Label */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Period Label</div>
          <div className="col-span-2 text-sm">{wizardState.periodLabel || 'N/A'}</div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Date Range</div>
          <div className="col-span-2 text-sm">
            {wizardState.startDate && wizardState.endDate
              ? `${format(wizardState.startDate, 'dd MMM yyyy')} - ${format(
                  wizardState.endDate,
                  'dd MMM yyyy'
                )}`
              : 'N/A'}
          </div>
        </div>

        {/* Months Covered */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Months Covered</div>
          <div className="col-span-2 text-sm">
            {wizardState.monthsCovered ? `${wizardState.monthsCovered} months` : 'N/A'}
          </div>
        </div>

        {/* Annualisation Factor (if applicable) */}
        {wizardState.annualisationFactor && (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-sm font-medium text-muted-foreground">
              Annualisation Factor
            </div>
            <div className="col-span-2 text-sm font-semibold">
              {wizardState.annualisationFactor.toFixed(2)}x
            </div>
          </div>
        )}

        <hr />

        {/* Entry Method */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-sm font-medium text-muted-foreground">Entry Method</div>
          <div className="col-span-2 text-sm">{getMethodLabel(wizardState.entryMethod)}</div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
            Back
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button
          onClick={onCreate}
          disabled={isSubmitting}
          data-testid="button-wizard-create"
        >
          {isSubmitting ? 'Creating...' : 'Create Period'}
        </Button>
      </div>
      </div>
    </TooltipProvider>
  );
};
